import React from 'react'
import { useTranslation } from 'react-i18next'
import { Plural } from 'react-i18next/icu.macro'
import {
  H3,
  Overline,
  Button,
  Separator,
  InfoSheetPanel,
  Row,
  usePageWidth,
  colors,
  DataBlock,
  Paragraph,
  useOverlay,
} from '@fs/zion-ui'
import { ContentAdd, Person } from '@fs/zion-icon'
import {
  getNestedElementById,
  getNestedFloatingElementsByType,
  getPersonName,
  getSexEnumFromResource,
  useSlsHighlightsContext,
  useSlsStateContext,
} from '@fs/zion-sls'
import { useFamilyReviewStateContext, useFamilyReviewDispatchContext, STEPS } from '../../providers/FamilyReviewContext'
import EventReviewCard from '../eventReviewCard/EventReviewCard'
import {
  showEssentialInfoHighlights,
  showAllNamesAndEventsHighlights,
  showEventsHighlights,
} from '../../utils/highlightUtils'
import { getPrimaryName } from '../../utils/utils'
import IsEverythingAccountedForCard from './IsEverythingAccountedForCard'
import RecordTooltip from './RecordTooltip'
import HelpWithThisStep from '../faqContent/HelpWithThisStep'

export default function StepThree({
  addFloatingEventOverlay,
  addNewEventOverlay,
  currentTask,
  stepFourOverlay,
  editEventOverlay,
  stepThreeOverlay,
  reviewPersonEventsOverlay,
  handleGoToNextRecord,
  hasImagePermission,
}) {
  const h = useSlsHighlightsContext()
  const [t] = useTranslation()
  const atPageWidth = usePageWidth()
  const isSmallScreen = atPageWidth({ default: true, md: false })
  const state = useFamilyReviewStateContext()
  const zionSLSState = useSlsStateContext()
  const dispatch = useFamilyReviewDispatchContext()
  const recordTooltipOverlay = useOverlay()

  const nextButtonClicked = () => {
    stepFourOverlay.open()
    dispatch({ type: 'SET_CURRENT_STEP', value: STEPS.STEP_FOUR })
    const allPersons = currentTask.subElements.filter((e) => e.elementType === 'PERSON')
    const personIds = allPersons.flatMap((person) => person?.id)
    showAllNamesAndEventsHighlights({
      h,
      currentTask,
      highlights: [...h.getHighlights().mainEditHighlights, ...h.getHighlights().splitScreenEditHighlights],
      personIds,
      isSmallScreen,
      splitScreenPartitionKey: state.splitScreenPartitionKey,
    })
  }

  const setHighlightsForNextCard = () => {
    const currentEventCardIndex = state.stepThree.eventCards.findIndex(
      (cardState) => cardState.eventId === state.selectedEventId
    )
    const nextEventCard = state.stepThree.eventCards[currentEventCardIndex + 1]
    if (nextEventCard)
      showEventsHighlights({
        h,
        currentTask,
        highlights: [...h.getHighlights().mainEditHighlights, ...h.getHighlights().splitScreenEditHighlights],
        eventIds: [nextEventCard.eventId],
        isSmallScreen,
        splitScreenPartitionKey: state.splitScreenPartitionKey,
      })
  }

  const handleCorrectClick = (eventId) => {
    dispatch({
      type: 'MARK_EVENT_CARD_CORRECT',
      value: eventId,
    })

    const updatedEventCards = state.stepThree.eventCards.map((card) =>
      card.eventId === eventId ? { ...card, hasBeenReviewed: true } : card
    )

    const allReviewed = updatedEventCards.every((card) => card.hasBeenReviewed)

    if (allReviewed) {
      showAllNamesAndEventsHighlights({
        h,
        currentTask,
        highlights: [...h.getHighlights().mainEditHighlights, ...h.getHighlights().splitScreenEditHighlights],
        personIds: currentTask.subElements.filter((e) => e.elementType === 'PERSON').map((person) => person?.id),
        isSmallScreen,
        splitScreenPartitionKey: state.splitScreenPartitionKey,
      })
    } else {
      setHighlightsForNextCard()
    }
  }

  const handleGoToNextRecordAndResetStep = () => {
    dispatch({ type: 'SET_CURRENT_STEP', value: STEPS.STEP_ONE })
    stepThreeOverlay.close()
    handleGoToNextRecord()
  }

  const handleNewEvent = () => {
    dispatch({ type: 'SET_ADD_NEW_EVENT_IDS' })
    const imageHasFloatingEvents = getNestedFloatingElementsByType(zionSLSState.images[0], 'EVENT').length !== 0
    if (imageHasFloatingEvents) addFloatingEventOverlay.open()
    else addNewEventOverlay.open()
    h.clearDisplay({ all: true })
  }

  const handleCloseStepThree = () => {
    stepThreeOverlay.close()
    dispatch({ type: 'SET_CURRENT_STEP', value: STEPS.STEP_TWO })
    showEssentialInfoHighlights({
      h,
      mainPartitionKey: currentTask.partitionKey,
      splitScreenPartitionKey: state.splitScreenPartitionKey,
      personIds: [state.stepTwo.relationshipCards[state.stepTwo.relationshipCards.length - 1].personId], // set highlights to last person
      isSmallScreen,
    })
  }

  const backButtonClicked = () => {
    handleCloseStepThree()
  }

  const userClosedPanel = () => {
    handleCloseStepThree()
  }

  const primaryPerson = getPrimaryName(currentTask)

  const getPersonIconColor = (person) => {
    const genderField = person?.subElements?.find((field) => field.fieldType === 'SEX_CODE')
    const gender = genderField ? getSexEnumFromResource(genderField) : 'unknown'
    if (gender === 'male') return colors.blue10
    if (gender === 'female') return colors.red10
    return colors.gray10
  }

  const groupEventsByPerson = (events) => {
    const groupedEvents = {}

    events.forEach((cardState) => {
      const event = getNestedElementById(currentTask?.subElements, cardState.eventId)
      if (event) {
        const personId = event.personId
        if (!groupedEvents[personId]) {
          groupedEvents[personId] = []
        }
        groupedEvents[personId].push(event)
      } else {
        // eslint-disable-next-line no-console
        console.warn(`Event with ID ${cardState.eventId} not found in currentTask subElements`)
      }
    })

    return groupedEvents
  }

  const inferredEvents = state.stepThree.eventCards.filter((cardState) => {
    const event = getNestedElementById(currentTask?.subElements, cardState.eventId)
    return event
  })

  const primaryEvent = inferredEvents.find((cardState) => {
    const event = getNestedElementById(currentTask?.subElements, cardState.eventId)
    return event?.primary === true
  })
  const primaryEventSls = getNestedElementById(currentTask?.subElements, primaryEvent?.eventId)

  const groupedEvents = groupEventsByPerson(
    inferredEvents.filter((cardState) => cardState.eventId !== primaryEvent?.eventId),
    currentTask
  )

  return (
    <InfoSheetPanel
      {...{ ...stepThreeOverlay, close: userClosedPanel }}
      headingColor="purple"
      title={t('family-review.step4.heading', 'Life Events')}
      footer={
        <>
          {!state.stepThree?.allReviewsComplete && (
            <>
              <Paragraph size="sm" secondary>
                {t(
                  'family-review.step3.must-review-all-events-message',
                  'You cannot move on until each event has been reviewed.'
                )}
              </Paragraph>
              <Separator size="sm" />
            </>
          )}
          <Row alignX="end">
            <Button onClick={backButtonClicked} data-testid="step-three-back-button" linkName="Step Three Go Back">
              {t('navigation.back-button', 'Back')}
            </Button>
            <Button
              disabled={!state.stepThree?.allReviewsComplete}
              emphasis="high"
              onClick={nextButtonClicked}
              linkName="Step Three Next"
              data-testid="step-three-next-button"
            >
              {t('navigation.next-button', 'Next')}
            </Button>
          </Row>
        </>
      }
    >
      {stepThreeOverlay.isOpen && (
        <>
          <H3 size="H5">
            <Overline>{t('family-review.stepXof4', 'Step {step} of 4', { step: '3' })}</Overline>
            {t('family-review.step3.subheading-short', 'Are these events correct for each person?')}
          </H3>
          <Separator size="xxs" />
          <RecordTooltip overlay={recordTooltipOverlay} />

          <HelpWithThisStep
            currentStep="3"
            hasImagePermission={hasImagePermission}
            handleGoToNextRecordAndResetStep={handleGoToNextRecordAndResetStep}
            personId={primaryPerson?.id}
          />
          <Separator size="xxs" />

          {primaryEvent && (
            <>
              <DataBlock
                heading={primaryPerson}
                data={getNestedElementById(currentTask?.subElements, primaryEvent.eventId).data}
              />
              <Separator size="nano" />
              <Paragraph size="sm">
                <Plural
                  i18nKey="family-review.step3.primary-event-linked-to-others"
                  count={(primaryEventSls?.superElements?.length ?? 1) - 1}
                  one="Event linked with # other"
                  other="Event linked with # others"
                />
              </Paragraph>
              <Separator size="nano" />
              <EventReviewCard
                event={getNestedElementById(currentTask?.subElements, primaryEvent.eventId)}
                currentTask={currentTask}
                editEventOverlay={editEventOverlay}
                handleCorrectClick={handleCorrectClick}
                reviewPersonEventsOverlay={reviewPersonEventsOverlay}
              />
            </>
          )}

          {Object.keys(groupedEvents).length > 0 && (
            <>
              <Separator size="xxs" />

              <H3 size="H5">{t('family-review.step3.inferred-text', 'Inferred events from the record')}</H3>
              {Object.keys(groupedEvents).map((personId) => {
                const person = currentTask.subElements.find((p) => p?.id === personId)
                const personName = getPersonName({ person, justOneName: true })
                const iconColor = getPersonIconColor(person)
                return (
                  <div key={personId}>
                    <Separator size="xs" />
                    <Row alignItemsY="middle" guttersX="nano">
                      <Person background={iconColor} />
                      <Paragraph size="sm">
                        <strong>{personName}</strong>
                      </Paragraph>
                    </Row>
                    <Separator size="xs" />

                    {groupedEvents[personId].map((event, index) => (
                      <div key={`${event?.id}-card`}>
                        <EventReviewCard
                          event={event}
                          currentTask={currentTask}
                          editEventOverlay={editEventOverlay}
                          handleCorrectClick={handleCorrectClick}
                          reviewPersonEventsOverlay={reviewPersonEventsOverlay}
                        />
                        {index < groupedEvents[personId].length - 1 && <Separator size="xs" />}
                      </div>
                    ))}
                  </div>
                )
              })}
            </>
          )}

          <Separator size="sm" />
          {!state.stepThree.allReviewsComplete && (
            <Button Icon={ContentAdd} onClick={handleNewEvent} data-testid="add-event" linkName="Step Three Add Event">
              {t('edit-index.add-event.button', 'Add Event')}
            </Button>
          )}
          <Separator size="sm" />

          {state.stepThree.allReviewsComplete && (
            <IsEverythingAccountedForCard
              overlay={recordTooltipOverlay}
              handleNewEntity={handleNewEvent}
              handleClick={nextButtonClicked}
              isStepThree
            />
          )}
        </>
      )}
    </InfoSheetPanel>
  )
}
